import type { CepResult } from "./cep";
import { BOOLEAN_YES, type Draft, HOW_FOUND_OTHER } from "./fields";
import type { PhotoData } from "./photo";

interface AddressParts {
	cep?: CepResult | null;
	cepRaw?: string;
	number?: string;
	complement?: string;
}

function composeAddress(parts: AddressParts): string | undefined {
	const segments: string[] = [];

	if (parts.cep && parts.cep.street.trim() !== "") {
		segments.push(parts.cep.street.trim());
	}

	const number = (parts.number ?? "").trim();

	if (number !== "") {
		segments.push(number);
	}

	const complement = (parts.complement ?? "").trim();

	if (complement !== "") {
		segments.push(complement);
	}

	if (parts.cep) {
		const tail = [parts.cep.district, parts.cep.city, parts.cep.uf]
			.map((s) => s.trim())
			.filter((s) => s !== "");

		if (tail.length > 0) {
			segments.push(tail.join(" - "));
		}
	}

	const cepRaw = (parts.cepRaw ?? "").trim();

	if (cepRaw !== "") {
		segments.push(`CEP ${cepRaw}`);
	}

	if (segments.length === 0) {
		return undefined;
	}

	return segments.join(", ");
}

export const FichaPayload = {
	build(args: {
		draft: Draft;
		cep: CepResult | null;
		accept: boolean;
		hp: string;
		photo: PhotoData;
	}) {
		const { draft, cep, accept, hp, photo } = args;

		const value = (key: keyof Draft) => {
			const raw = (draft[key] ?? "").trim();

			return raw === "" ? undefined : raw;
		};

		const address = composeAddress({
			cep,
			cepRaw: value("cep"),
			number: draft.address_number,
			complement: draft.address_complement,
		});

		const hasInsurance = draft.has_insurance === BOOLEAN_YES;

		return {
			name: (draft.name ?? "").trim(),
			birth_date: value("birth_date"),
			cpf: (draft.cpf ?? "").trim(),
			rg: value("rg"),
			mother_name: value("mother_name"),
			phone: value("phone"),
			email: value("email"),
			preferred_channel: value("preferred_channel"),
			cep: value("cep"),
			address,
			occupation: value("occupation"),
			marital_status: value("marital_status"),
			has_insurance: hasInsurance,
			insurance: hasInsurance ? value("insurance") : undefined,
			how_found: value("how_found"),
			how_found_detail: draft.how_found === HOW_FOUND_OTHER ? value("how_found_other") : undefined,
			accept_marketing: accept,
			photo,
			hp: hp.trim() === "" ? undefined : hp,
		};
	},
};
